import React, {useRef, useState} from 'react';
import {Paper, Stack, TextField, Typography} from "@mui/material"
import AsyncButton from "../button/AsyncButton"
import ApiError from "../../api/ApiError"
import {useNavigate} from "react-router-dom"
import useEnterShortcut from "../../hooks/useEnterShortcut"
import {ProjectRequests} from "../../api/requests/ProjectRequests"
import {ModalOpenProps} from "../modal/ModalOpenProps"
import ModalBase from "../modal/ModalBase"
import {ProjectType, resolveDescriptionForProjectType, resolveNameForProjectType} from "../../api/enum/ProjectType"
import ProjectTypeIcon from "./ProjectTypeIcon"

const PROJECT_TYPES = [ProjectType.CLASSIFIER, ProjectType.FREE_TEXT]

function NewProjectModal({open, setOpen}: ModalOpenProps) {
    const [name, setName] = useState("")
    const [type, setType] = useState<ProjectType>(ProjectType.CLASSIFIER)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<ApiError>()

    const navigate = useNavigate()

    const disabled = name.length === 0 || name.length > 300

    const contentRef = useRef<HTMLDivElement>(null)

    useEnterShortcut(contentRef, () => {
        if (open && !disabled) {
            handleCreate()
        }
    })

    const handleCreate = () => {
        setSaving(true)
        ProjectRequests.create(name, type)
            .then(project => {
                navigate(`/projects/${project.id}`)
            })
            .catch(setError)
            .finally(() => setSaving(false))
    }

    return (
        <ModalBase
            title="New Project"
            size="large"
            open={open}
            setOpen={setOpen}
            buttons={
                <AsyncButton variant="contained"
                             disabled={disabled}
                             waiting={saving}
                             onClick={() => handleCreate()}
                >
                    Create
                </AsyncButton>
            }
            error={error}
        >
            <Stack spacing={3} ref={contentRef}>
                <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                    {PROJECT_TYPES.map(candidate => {
                        const selected = candidate === type
                        return (
                            <Paper key={candidate}
                                   variant="outlined"
                                   onClick={() => setType(candidate)}
                                   sx={{
                                       flex: 1,
                                       p: 2,
                                       cursor: "pointer",
                                       borderColor: selected ? "primary.main" : "divider",
                                       bgcolor: selected ? "action.selected" : "background.paper",
                                   }}>
                                <Stack spacing={0.5} alignItems="flex-start">
                                    <ProjectTypeIcon type={candidate} color={selected ? "primary" : "action"}/>
                                    <Typography sx={{fontWeight: 600}}>{resolveNameForProjectType(candidate)}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {resolveDescriptionForProjectType(candidate)}
                                    </Typography>
                                </Stack>
                            </Paper>
                        )
                    })}
                </Stack>

                <TextField label="Name"
                           autoComplete="project-name"
                           multiline
                           value={name}
                           onChange={e => setName(e.target.value.slice(0, 300))}
                />
            </Stack>
        </ModalBase>
    );
}

export default NewProjectModal;
